import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, EVENT_LABEL } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Spinner, EmptyState, Button } from "../components/ui";
import { useAuth } from "../auth";
import { exportAttendancePdf } from "../lib/pdf";
import { Icon } from "../components/icons";
import { STATUS_PT } from "../components/Presence";

// Official (validated) classification; the singer's own declaration is shown separately.
const statusOf = (e) => (e.validated && e.validated_status) || e.status || (e.present ? "present" : "expected");
const groupOf = (s) => (s === "presente" ? "present" : ["present", "justified", "unjustified", "excused"].includes(s) ? s : "pending");
const GROUPS = [
  { k: "present", label: "Presenças" },
  { k: "justified", label: "Faltas justificadas" },
  { k: "unjustified", label: "Faltas injustificadas" },
  { k: "excused", label: "Dispensas" },
  { k: "pending", label: "Por validar" },
];
const PILL = { present: "done", justified: "doing", excused: "doing", unjustified: "todo", pending: "todo" };

export default function Attendance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("estado") || "";
  const focus = sp.get("evento");
  const q = useQuery({ queryKey: ["me-attendance"], queryFn: () => api("/me/attendance") });
  const data = q.data || {};
  const summary = data.summary || data;
  const events = (data.records || data.events || []).map((r) => (r.event ? { ...r.event, ...r, id: r.event.id } : r));
  const counts = Object.fromEntries(GROUPS.map((g) => [g.k, events.filter((e) => groupOf(statusOf(e)) === g.k).length]));
  const present = summary.present ?? counts.present;
  const absent = (summary.justified ?? counts.justified) + (summary.unjustified ?? counts.unjustified);
  const total = summary.total ?? events.length;
  const rate = Math.max(0, Math.min(100, summary.rate != null ? summary.rate : total ? Math.round((present / total) * 100) : 0));
  const shown = filter ? events.filter((e) => groupOf(statusOf(e)) === filter) : events;
  const setFilter = (k) => { const n = new URLSearchParams(sp); if (!k || k === filter) n.delete("estado"); else n.set("estado", k); n.delete("evento"); setSp(n, { replace: true }); };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/perfil"))}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ fontSize: 26, flex: 1 }}>A minha assiduidade</h1>
        <Button className="auto sm ghost" icon="download" disabled={q.isLoading} data-testid="attendance-pdf"
          onClick={() => exportAttendancePdf(user?.name || "", { rate, present, absent, total }, shown.map((e) => ({ starts_at: e.starts_at, title: e.title || EVENT_LABEL[e.type] || "Evento", status: STATUS_PT[statusOf(e)] || statusOf(e) })))}>PDF</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 720 }}>
          <div className="card" style={{ padding: 16, display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }} data-testid="attendance-rate">
            <div style={{ fontFamily: "var(--f-light)", fontSize: 36 }}>{rate}%</div>
            <div className="t-hint">de presença em {total} {total === 1 ? "evento" : "eventos"}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: 8, marginBottom: 20 }}>
            {GROUPS.map((g) => (
              <button key={g.k} className="card" onClick={() => setFilter(g.k)} aria-pressed={filter === g.k} data-testid={`attendance-filter-${g.k}`}
                style={{ padding: 12, textAlign: "center", cursor: "pointer", color: "inherit", ...(filter === g.k ? { borderColor: "var(--brand-primary)", background: "var(--brand-tertiary)" } : {}) }}>
                <div style={{ fontFamily: "var(--f-light)", fontSize: 26 }}>{counts[g.k]}</div>
                <div className="t-hint">{g.label}</div>
              </button>
            ))}
          </div>
          {filter ? (
            <div className="row-between" style={{ marginBottom: 10 }}>
              <span className="t-section" data-testid="attendance-filter-label">{GROUPS.find((g) => g.k === filter)?.label}</span>
              <button className="chip" onClick={() => setFilter("")} data-testid="attendance-filter-clear"><Icon name="close" size={13} /> VER TODOS</button>
            </div>
          ) : null}
          {shown.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {shown.map((e, i) => (
                <Link key={e.id || i} to={`/events/${e.id}`} className="card" data-testid={`attendance-row-${e.id || i}`}
                  style={{ padding: 14, display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "inherit", ...(focus === e.id ? { borderColor: "var(--brand-primary)" } : {}) }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{e.title || EVENT_LABEL[e.type] || "Evento"}</div>
                    {e.starts_at ? <div className="t-hint">{fmtDateTime(e.starts_at)}</div> : null}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <span className={`pill ${PILL[groupOf(statusOf(e))]}`} data-testid={`attendance-status-${e.id || i}`}>{(STATUS_PT[statusOf(e)] || statusOf(e)).toUpperCase()}</span>
                    {e.declared_status === "absent" || (!e.declared_status && (e.reason || e.requested_at)) ? <span className="t-hint" style={{ fontSize: 11 }} data-testid={`attendance-declared-${e.id || i}`}>Declarou ausência{e.declared_at ? ` em ${new Date(e.declared_at).toLocaleDateString("pt-PT", { timeZone: "Europe/Lisbon" })}` : ""}</span> : null}
                    {e.declared_status === "attending" ? <span className="t-hint" style={{ fontSize: 11 }}>Confirmou presença</span> : null}
                    <span className="t-hint" style={{ fontSize: 10 }}>{e.validated ? "Validado pela direcção" : "Por validar"}</span>
                  </div>
                  <Icon name="chevron-forward" size={16} color="var(--muted)" />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon="stats" text={filter ? "Sem eventos nesta categoria." : "Ainda sem registos de assiduidade."} testid="attendance-empty" />
          )}
        </div>
      )}
    </>
  );
}
